import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { ExtensionService } from "src/app/core/core-services/services/extension.service";
import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { DisabledSiteLists, MetaDataResponse } from "./extension-settings.models";

@Injectable({ providedIn: "root" })
export class ExtensionSettingsService {
  constructor(
    private readonly dataService: DataService,
    private readonly extensionService: ExtensionService,
    private readonly notification: NotificationService
  ) {}

  loadMetaData(): Observable<MetaDataResponse> {
    return this.dataService
      .get({ url: "v2/meta-data", isLoader: false })
      .pipe(map((response: any) => response?.result));
  }

  saveFlyGrammar(enabled: boolean, language: string | null): Observable<any> {
    return this.saveSettings(
      {
        fly_grammar: enabled ? "enabled" : "disabled",
        fly_grammar_default_language: language || null,
      },
      "FlyGrammar status updated successfully!"
    );
  }

  saveFlyRewrite(enabled: boolean): Observable<any> {
    return this.saveSettings(
      { fly_rewrite: enabled ? "enabled" : "disabled" },
      "FlyMSG AI Paragraph Rewriter status updated successfully!"
    );
  }

  saveSentenceRewrite(enabled: boolean): Observable<any> {
    return this.saveSettings(
      { fly_sentence_rewrite: enabled ? "enabled" : "disabled" },
      "FlyMSG Sentence Rewriter status updated successfully!"
    );
  }

  saveTypingSettings(speed: number, style: string, isDefault: boolean): Observable<any> {
    return this.saveSettings(
      { typing_speed: speed, typing_style: style },
      isDefault ? "Typing Settings set to default" : "Typing Settings updated successfully!"
    );
  }

  saveTimeout(timeout: number, isDefault: boolean): Observable<any> {
    return this.saveSettings(
      { shortcut_timeout: timeout },
      isDefault ? "Timeout set to default" : "FlyCut timeout updated successfully!"
    );
  }

  /**
   * One-time migration: accounts created before the default changed still
   * carry the legacy 750ms FlyCut timeout — bump them to the 5000ms default.
   * Called from the dashboard on load.
   */
  migrateLegacyTimeout(currentTimeout: number): void {
    if (currentTimeout == 750) {
      this.saveTimeout(5000, true).subscribe();
    }
  }

  /**
   * Persist the per-site "Disabled sites" lists. Pass only the lists that
   * changed — the endpoint accepts partial multi-key bodies, so a master-switch
   * flip that touches all four lists is still ONE round-trip (and one
   * `sendUpdateMessage` ping to the extension).
   */
  saveDisabledSites(lists: Partial<DisabledSiteLists>): Observable<any> {
    return this.saveSettings({ ...lists }, "Disabled sites updated successfully!");
  }

  saveCsvSplitSeparator(separator: string | null): Observable<any> {
    return this.saveSettings(
      { csv_split_separator: separator },
      "CSV split separator updated successfully!"
    );
  }

  checkExtensionInstalled(): Promise<boolean> {
    return this.extensionService.isExtensionInstalled();
  }

  getExtensionVersion(): Promise<number> {
    return this.extensionService.getVersion();
  }

  get isChromeBrowser(): boolean {
    return this.extensionService.isChromeBrowser;
  }

  private saveSettings(data: Record<string, any>, toastMessage: string): Observable<any> {
    return this.dataService.put({ url: "v1/user/setting", data }).pipe(
      tap((response: any) => {
        if (response?.result) {
          this.extensionService.sendUpdateMessage();
          this.notification.toast(toastMessage);
        }
      })
    );
  }
}
